import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../utils/api';
import { Settings2, Shield, Code2, Flag, CheckCircle, Trash2, Plus, Users, BarChart3, CreditCard, Building2 } from 'lucide-react';
import useSWR from 'swr';
import PageHeader from '../components/admin/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  PartnersTab, 
  AnalyticsTab, 
  BillingTab, 
  DocsTab, 
  CreatePartnerModal,
  OrganizationsTab
} from '../components/developer-portal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetcher = async (url) => {
  const token = localStorage.getItem('admin_token');
  const res = await fetch(`${API_URL}${url}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return null;
  return res.json();
};

const settingsTabs = [
  { id: 'general', name: 'General', icon: Settings2, path: '' },
  { id: 'moderation', name: 'Moderation', icon: Shield, path: 'moderation' },
  { id: 'developers', name: 'Developers', icon: Code2, path: 'developers' },
];

function GeneralSettings() {
  const { token } = useAuth();
  const [currentAvatar, setCurrentAvatar] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { loadCurrentAvatar(); }, []);

  const loadCurrentAvatar = async () => {
    try {
      const response = await fetch('/api/admin/settings/avatar', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { const data = await response.json(); setCurrentAvatar(data.avatar_url); }
    } catch (error) { console.error('Error loading avatar:', error); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { alert('Image must be less than 2MB'); return; }
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!previewUrl) return;
    setUploading(true);
    try {
      const response = await fetch('/api/admin/settings/avatar', { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ avatar_data: previewUrl }) });
      if (response.ok) { const data = await response.json(); setCurrentAvatar(data.avatar_url); setPreviewUrl(''); alert('Avatar updated successfully!'); }
      else { const error = await response.json(); alert(error.detail || 'Failed to update avatar'); }
    } catch (error) { alert('Failed to upload avatar'); }
    finally { setUploading(false); }
  };

  const handleReset = async () => {
    if (!confirm('Reset to the default Squadline logo?')) return;
    setUploading(true);
    try {
      const response = await fetch('/api/admin/settings/avatar/reset', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) { const data = await response.json(); setCurrentAvatar(data.avatar_url); setPreviewUrl(''); alert('Avatar reset to default'); }
    } catch (error) { console.error('Error resetting avatar:', error); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>Squadline Avatar</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-6">This avatar appears on posts created from the admin portal and represents the Squadline brand in the app.</p>
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0">
              <p className="text-sm font-medium text-gray-800 mb-2">Current Avatar</p>
              <div className="w-32 h-32 rounded-2xl overflow-hidden bg-gray-100 border-2 border-gray-200">
                {currentAvatar ? <img src={currentAvatar} alt="Current Squadline Avatar" className="w-full h-full object-cover" /> : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800 mb-2">Upload New Avatar</p>
              {previewUrl && (
                <div className="mb-4">
                  <div className="w-32 h-32 rounded-2xl overflow-hidden bg-gray-100 border-2 border-indigo-500"><img src={previewUrl} alt="Preview" className="w-full h-full object-cover" /></div>
                  <p className="text-xs text-emerald-600 mt-1">Preview - Click "Save" to apply</p>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>Choose Image</Button>
                {previewUrl && (
                  <>
                    <Button onClick={handleUpload} disabled={uploading}>{uploading ? 'Saving...' : 'Save Avatar'}</Button>
                    <Button variant="ghost" onClick={() => setPreviewUrl('')}>Cancel</Button>
                  </>
                )}
                <Button variant="ghost" onClick={handleReset} disabled={uploading}>Reset to Default</Button>
              </div>
              <p className="text-xs text-gray-500 mt-4">Recommended: Square image, at least 256x256 pixels. Max: 2MB.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>App Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-gray-500">App Name</p><p className="font-medium text-gray-900">Squadline</p></div>
            <div><p className="text-gray-500">Version</p><p className="font-medium text-gray-900">1.0.0</p></div>
            <div><p className="text-gray-500">Environment</p><p className="font-medium text-gray-900">{window.location.hostname.includes('preview') ? 'Preview' : 'Production'}</p></div>
            <div><p className="text-gray-500">API Status</p><p className="font-medium text-emerald-600">Connected</p></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ModerationSettings() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadPosts(); }, []);

  const loadPosts = async () => {
    try { const data = await apiRequest('/api/admin/moderation/posts'); setPosts(data.posts || []); }
    catch (err) { console.error('Error loading posts:', err); }
    finally { setLoading(false); }
  };

  const handleApprove = async (postId) => {
    try { await apiRequest(`/api/admin/moderation/posts/${postId}/approve`, { method: 'POST' }); setPosts(posts.filter(p => p.post.post_id !== postId)); }
    catch (err) { alert(err.message); }
  };

  const handleRemove = async (postId) => {
    if (!confirm('Are you sure you want to remove this post?')) return;
    try { await apiRequest(`/api/admin/moderation/posts/${postId}/remove`, { method: 'POST' }); setPosts(posts.filter(p => p.post.post_id !== postId)); }
    catch (err) { alert(err.message); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="space-y-6">
      <div><h2 className="text-lg font-semibold text-gray-900">Content Moderation</h2><p className="text-sm text-gray-500">Review flagged and reported content</p></div>
      {posts.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Flag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No flagged content</h3>
          <p className="text-gray-500 mt-1">All content is clean!</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {posts.map(({ post, author }) => (
            <Card key={post.post_id}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    {author?.picture ? <img src={author.picture} alt="" className="w-10 h-10 rounded-full" /> : <span className="text-indigo-600 font-medium">{author?.name?.charAt(0) || '?'}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900">{author?.name || 'Unknown'}</p>
                      <span className="text-sm text-gray-500">{author?.email}</span>
                    </div>
                    <p className="text-gray-700 mb-3">{post.content}</p>
                    {post.image && <img src={post.image} alt="" className="max-w-xs rounded-lg mb-3" />}
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Flag className="w-4 h-4 text-rose-500" />
                      <span>{post.reports?.length || 0} reports</span>
                      <span>•</span>
                      <span>{post.created_at ? new Date(post.created_at).toLocaleDateString() : '-'}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" className="text-emerald-600 hover:bg-emerald-50" onClick={() => handleApprove(post.post_id)} title="Approve"><CheckCircle className="w-5 h-5" /></Button>
                    <Button variant="ghost" size="icon" className="text-rose-500 hover:bg-rose-50" onClick={() => handleRemove(post.post_id)} title="Remove"><Trash2 className="w-5 h-5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DeveloperSettings() {
  const [activeTab, setActiveTab] = useState('partners');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(7);

  const { data: partnersData, mutate: refreshPartners } = useSWR('/api/integrations/partners', fetcher, { revalidateOnFocus: false });
  const { data: analyticsData, isLoading: analyticsLoading } = useSWR(`/api/integrations/analytics?days=${analyticsDays}`, fetcher, { revalidateOnFocus: false });
  const { data: pricingData } = useSWR('/api/integrations/billing/pricing', fetcher, { revalidateOnFocus: false });
  const { data: webhookConfig } = useSWR('/api/admin/stripe/webhook-config', fetcher, { revalidateOnFocus: false });
  const { data: orgsData, isLoading: orgsLoading, mutate: refreshOrgs } = useSWR('/api/integrations/admin/organizations', fetcher, { revalidateOnFocus: false });

  const partners = partnersData?.partners || [];
  const organizations = orgsData?.organizations || [];

  const copyToClipboard = async (text, field) => { await navigator.clipboard.writeText(text); setCopiedField(field); setTimeout(() => setCopiedField(null), 2000); };

  const handleCreatePartner = async (e) => {
    e.preventDefault(); setLoading(true);
    const formData = new FormData(e.target);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/integrations/partners`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ name: formData.get('name'), contact_email: formData.get('contact_email'), description: formData.get('description') }) });
      if (!res.ok) throw new Error('Request failed');
      setNewCredentials(await res.json()); refreshPartners();
    } catch (err) { alert('Error: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleDeactivate = async (partnerId, partnerName) => {
    if (!window.confirm(`Deactivate "${partnerName}"?`)) return;
    try { const token = localStorage.getItem('admin_token'); await fetch(`${API_URL}/api/integrations/partners/${partnerId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); refreshPartners(); }
    catch (err) { alert('Error: ' + err.message); }
  };

  const handleAddSeats = async (partnerId, partnerName) => {
    const quantity = prompt(`Seats to add to "${partnerName}"?`, '10'); if (!quantity) return;
    const reason = prompt('Reason:', 'Manual adjustment'); if (reason === null) return;
    try { const token = localStorage.getItem('admin_token'); const res = await fetch(`${API_URL}/api/integrations/billing/add-seats-admin?partner_id=${partnerId}&quantity=${quantity}&reason=${encodeURIComponent(reason)}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); const result = await res.json(); alert(`Added ${result.seats_added} seats. New balance: ${result.new_balance}`); refreshPartners(); }
    catch (err) { alert('Error: ' + err.message); }
  };

  const handleAddOrgSeats = async (organizationId, orgName) => {
    const quantity = prompt(`Seats to add to "${orgName}"?`, '10'); if (!quantity) return;
    const reason = prompt('Reason:', 'Manual adjustment'); if (reason === null) return;
    try { const token = localStorage.getItem('admin_token'); const res = await fetch(`${API_URL}/api/integrations/organizations/billing/add-seats-admin?organization_id=${organizationId}&quantity=${quantity}&reason=${encodeURIComponent(reason)}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); const result = await res.json(); alert(`Added ${result.seats_added} seats to ${result.organization_name}. New balance: ${result.new_balance}`); refreshOrgs(); }
    catch (err) { alert('Error: ' + err.message); }
  };

  const handleDeleteOrg = async (organizationId, orgName) => {
    const input = prompt(`Delete "${orgName}"?\n\n- OK = Deactivate (soft delete)\n- Type "PERMANENT" to permanently delete`, 'deactivate'); if (!input) return;
    const permanent = input.toUpperCase() === 'PERMANENT';
    if (permanent && !window.confirm(`⚠️ PERMANENT DELETE - This cannot be undone. Continue?`)) return;
    try { const token = localStorage.getItem('admin_token'); const res = await fetch(`${API_URL}/api/integrations/admin/organizations/${organizationId}?permanent=${permanent}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); const result = await res.json(); if (result.success) { alert(`${result.action === 'permanent_delete' ? '🗑️ Permanently deleted' : '✓ Deactivated'}: ${result.organization_name}`); refreshOrgs(); } else { alert('Error: ' + (result.detail || 'Unknown error')); } }
    catch (err) { alert('Error: ' + err.message); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div><h2 className="text-lg font-semibold text-gray-900">Developer Portal</h2><p className="text-sm text-gray-500">Manage integration partners and API access</p></div>
        <Button onClick={() => setShowCreateModal(true)}><Plus className="w-4 h-4 mr-2" />New Partner</Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="partners"><Users className="w-4 h-4 mr-1" />Partners</TabsTrigger>
          <TabsTrigger value="organizations"><Building2 className="w-4 h-4 mr-1" />Organizations</TabsTrigger>
          <TabsTrigger value="analytics"><BarChart3 className="w-4 h-4 mr-1" />Analytics</TabsTrigger>
          <TabsTrigger value="billing"><CreditCard className="w-4 h-4 mr-1" />Billing</TabsTrigger>
          <TabsTrigger value="docs"><Code2 className="w-4 h-4 mr-1" />Docs</TabsTrigger>
        </TabsList>
        <TabsContent value="partners"><PartnersTab partners={partners} copiedField={copiedField} copyToClipboard={copyToClipboard} handleDeactivate={handleDeactivate} handleAddSeats={handleAddSeats} /></TabsContent>
        <TabsContent value="organizations"><OrganizationsTab organizations={organizations} partners={partners} loading={orgsLoading} onAddSeats={handleAddOrgSeats} onDeleteOrg={handleDeleteOrg} stats={orgsData || {}} /></TabsContent>
        <TabsContent value="analytics"><AnalyticsTab analyticsData={analyticsData} analyticsLoading={analyticsLoading} analyticsDays={analyticsDays} setAnalyticsDays={setAnalyticsDays} /></TabsContent>
        <TabsContent value="billing"><BillingTab partners={partners} pricingData={pricingData} webhookConfig={webhookConfig} handleAddSeats={handleAddSeats} /></TabsContent>
        <TabsContent value="docs"><DocsTab /></TabsContent>
      </Tabs>

      <CreatePartnerModal showCreateModal={showCreateModal} setShowCreateModal={setShowCreateModal} newCredentials={newCredentials} setNewCredentials={setNewCredentials} copiedField={copiedField} copyToClipboard={copyToClipboard} loading={loading} handleCreatePartner={handleCreatePartner} />
    </div>
  );
}

function Settings() {
  const location = useLocation();
  const currentPath = location.pathname.replace('/settings', '').replace('/', '') || 'general';
  const activeTab = currentPath === 'moderation' ? 'moderation' : currentPath === 'developers' ? 'developers' : 'general';

  return (
    <div className="flex gap-8">
      <div className="w-56 flex-shrink-0">
        <Card className="sticky top-6">
          <CardContent className="p-2">
            <nav className="space-y-1">
              {settingsTabs.map((tab) => {
                const isActive = activeTab === tab.id;
                const to = tab.path ? `/settings/${tab.path}` : '/settings';
                return (
                  <Link key={tab.id} to={to}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
                    <tab.icon className="w-5 h-5" />
                    <span className="font-medium">{tab.name}</span>
                  </Link>
                );
              })}
            </nav>
          </CardContent>
        </Card>
      </div>

      <div className="flex-1 min-w-0">
        <PageHeader title="Settings" description="Configure app settings and preferences" />
        <Routes>
          <Route index element={<GeneralSettings />} />
          <Route path="moderation" element={<ModerationSettings />} />
          <Route path="developers" element={<DeveloperSettings />} />
          <Route path="*" element={<Navigate to="/settings" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default Settings;
